import { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Typography,
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import {
  getEmployees,
  createRequest,
  getRequests,
  updateRequest,
  deleteRequest,
} from '../services/api';
import { format } from 'date-fns';

const Requests = () => {
  const [employees, setEmployees] = useState([]);
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [tabValue, setTabValue] = useState('all');
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({
    employeeId: '',
    type: 'vacation',
    startDate: '',
    endDate: '',
    reason: '',
  });

  useEffect(() => {
    loadEmployees();
    loadRequests();
  }, []);

  useEffect(() => {
    if (tabValue === 'all') {
      setFilteredRequests(requests);
    } else {
      setFilteredRequests(requests.filter(req => req.status === tabValue));
    }
  }, [requests, tabValue]);

  const loadEmployees = async () => {
    try {
      const { data } = await getEmployees();
      setEmployees(data);
    } catch (error) {
      console.error('Error al cargar empleados:', error);
    }
  };

  const loadRequests = async () => {
    try {
      const { data } = await getRequests();
      setRequests(data);
    } catch (error) {
      console.error('Error al cargar solicitudes:', error);
    }
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormData({
      employeeId: '',
      type: 'vacation',
      startDate: '',
      endDate: '',
      reason: '',
    });
  };

  const handleChange = e => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async () => {
    try {
      await createRequest(formData);
      handleCloseDialog();
      loadRequests();
    } catch (error) {
      console.error('Error al crear solicitud:', error);
      alert('Error al crear solicitud');
    }
  };

  const handleApprove = async id => {
    try {
      await updateRequest(id, { status: 'approved' });
      loadRequests();
    } catch (error) {
      console.error('Error al aprobar solicitud:', error);
      alert('Error al aprobar solicitud');
    }
  };

  const handleReject = async id => {
    const notes = prompt('Motivo del rechazo (opcional):');
    try {
      await updateRequest(id, {
        status: 'rejected',
        reviewNotes: notes,
      });
      loadRequests();
    } catch (error) {
      console.error('Error al rechazar solicitud:', error);
      alert('Error al rechazar solicitud');
    }
  };

  const handleDelete = async id => {
    if (window.confirm('¿Eliminar esta solicitud?')) {
      try {
        await deleteRequest(id);
        loadRequests();
      } catch (error) {
        console.error('Error al eliminar solicitud:', error);
        alert('Error al eliminar solicitud');
      }
    }
  };

  const getStatusColor = status => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = status => {
    switch (status) {
      case 'pending':
        return 'Pendiente';
      case 'approved':
        return 'Aprobado';
      case 'rejected':
        return 'Rechazado';
      default:
        return status;
    }
  };

  const getTypeLabel = type => {
    switch (type) {
      case 'vacation':
        return 'Vacaciones';
      case 'permission':
        return 'Permiso';
      case 'sick_leave':
        return 'Licencia médica';
      case 'other':
        return 'Otro';
      default:
        return type;
    }
  };

  return (
    <Container maxWidth="lg">
      <Box
        sx={{
          mb: 4,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h4">Solicitudes y Permisos</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
        >
          Nueva Solicitud
        </Button>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="Todas" value="all" />
          <Tab label="Pendientes" value="pending" />
          <Tab label="Aprobadas" value="approved" />
          <Tab label="Rechazadas" value="rejected" />
        </Tabs>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Empleado</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Fecha Inicio</TableCell>
              <TableCell>Fecha Fin</TableCell>
              <TableCell>Motivo</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRequests.map(request => (
              <TableRow key={request._id}>
                <TableCell>
                  {request.employee.firstName} {request.employee.lastName}
                </TableCell>
                <TableCell>
                  <Chip label={getTypeLabel(request.type)} size="small" />
                </TableCell>
                <TableCell>
                  {format(new Date(request.startDate), 'dd/MM/yyyy')}
                </TableCell>
                <TableCell>
                  {format(new Date(request.endDate), 'dd/MM/yyyy')}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                    {request.reason}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={getStatusLabel(request.status)}
                    color={getStatusColor(request.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {request.status === 'pending' && (
                    <>
                      <IconButton
                        size="small"
                        color="success"
                        onClick={() => handleApprove(request._id)}
                      >
                        <CheckCircleIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleReject(request._id)}
                      >
                        <CancelIcon />
                      </IconButton>
                    </>
                  )}
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDelete(request._id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Nueva Solicitud</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              select
              margin="normal"
              label="Empleado"
              name="employeeId"
              value={formData.employeeId}
              onChange={handleChange}
              required
            >
              {employees.map(employee => (
                <MenuItem key={employee._id} value={employee._id}>
                  {employee.firstName} {employee.lastName}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              select
              margin="normal"
              label="Tipo de Solicitud"
              name="type"
              value={formData.type}
              onChange={handleChange}
              required
            >
              <MenuItem value="vacation">Vacaciones</MenuItem>
              <MenuItem value="permission">Permiso</MenuItem>
              <MenuItem value="sick_leave">Licencia médica</MenuItem>
              <MenuItem value="other">Otro</MenuItem>
            </TextField>
            <TextField
              fullWidth
              margin="normal"
              label="Fecha de Inicio"
              name="startDate"
              type="date"
              value={formData.startDate}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              label="Fecha de Fin"
              name="endDate"
              type="date"
              value={formData.endDate}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              label="Motivo"
              name="reason"
              multiline
              rows={4}
              value={formData.reason}
              onChange={handleChange}
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained">
            Crear Solicitud
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Requests;
